import { useQuery } from '@tanstack/react-query';
import { BookOpen, GraduationCap } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';

/**
 * Courses at a glance — course count + total enrollments + the latest students.
 * Wraps the existing /admin/courses and /admin/enrollments list endpoints
 * (their `pagination.total` gives the counts without fetching every row).
 */
interface CoursesResp {
  courses: unknown[];
  pagination: { total: number };
}
interface Enrollment {
  id: string;
  student_name: string;
  course_name: string;
  status: string;
}
interface EnrollmentsResp {
  enrollments: Enrollment[];
  pagination: { total: number };
}

export default function CoursesSummaryWidget() {
  const courses = useQuery({
    queryKey: ['dashboard', 'courses'],
    queryFn: () => api.get<CoursesResp>('/admin/courses?limit=1'),
    staleTime: 60000,
  });
  const enrollments = useQuery({
    queryKey: ['dashboard', 'enrollments'],
    queryFn: () => api.get<EnrollmentsResp>('/admin/enrollments?limit=5'),
    staleTime: 60000,
  });

  const recent = enrollments.data?.enrollments ?? [];

  return (
    <WidgetShell
      title="Courses"
      icon={<GraduationCap />}
      to="/admin/courses"
      isLoading={courses.isLoading || enrollments.isLoading}
      error={courses.error || enrollments.error}
      isEmpty={!!courses.data && courses.data.pagination.total === 0}
      emptyMessage="No courses yet"
      emptyIcon={<BookOpen />}
      skeletonClassName="h-40"
    >
      {courses.data && enrollments.data && courses.data.pagination.total > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <StatCard title="Courses" value={courses.data.pagination.total} icon={<BookOpen />} tone="indigo" />
            <StatCard
              title="Enrollments"
              value={enrollments.data.pagination.total}
              icon={<GraduationCap />}
              tone="green"
            />
          </div>
          {recent.length > 0 && (
            <ul className="divide-y divide-line">
              {recent.slice(0, 4).map((e) => (
                <li key={e.id} className="flex items-center gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{e.student_name}</p>
                    <p className="truncate text-xs text-stone-500">{e.course_name}</p>
                  </div>
                  <span className="shrink-0 text-xs font-medium capitalize text-stone-500">{e.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </WidgetShell>
  );
}
